import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';
import {
    VictoryBar,
    VictoryChart,
    VictoryAxis,
    VictoryTheme,
    VictoryTooltip,
    VictoryArea,
    VictoryPolarAxis,
    VictoryScatter,
} from 'victory';
import { AuthContext } from '../AuthContext';
import Navbar from './Navbar';
import trackerConfig from './tracker.json';


const ANALYTICS_ENDPOINT = 'https://analytics.insightos.co.uk/data/bucket';

const BUCKET_LABELS = Object.entries(trackerConfig?.buckets || {}).reduce((map, [key, value]) => {
    map[key] = value?.label || key;
    return map;
}, {});

const BUCKET_COLOR_MAP = {
    hr: '217, 70, 89', // rose
    communication: '14, 165, 233', // sky
    'task-management': '99, 102, 241', // indigo
};
const DEFAULT_BUCKET_COLOR = '79, 70, 229';
const BUCKET_CIRCLE_POSITIONS = [
    { bucketKey: 'hr', angle: -90 },
    { bucketKey: 'communication', angle: 30 },
    { bucketKey: 'task-management', angle: 210 },
];

const HIGHLIGHT_COMPONENT_GROUPS = [
    {
        id: 'shifts',
        label: 'Shifts',
        description: 'publish-shifts, shift-copy',
        components: ['publish-shifts', 'shift-copy'],
    },
    {
        id: 'messages-sent',
        label: 'Messages Sent',
        description: 'message-sent',
        components: ['message-sent'],
    },
    {
        id: 'holidays-requested',
        label: 'Holidays Requested',
        description: 'holiday-request',
        components: ['holiday-request'],
    },
    {
        id: 'interviews',
        label: 'Interviews',
        description: 'interview-meeting-form',
        components: ['interview-meeting-form'],
    },
    {
        id: 'overtime-requested',
        label: 'Overtime Requested',
        description: 'overtime-manager-form',
        components: ['overtime-manager-form'],
    },
    {
        id: 'tasks-saved-today',
        label: 'Tasks Saved Today',
        description: 'save-tasks',
        components: ['save-tasks'],
    },
    {
        id: 'help-submitted',
        label: 'Help Submitted',
        description: 'bottleneck-help-actions',
        components: ['bottleneck-help-actions'],
    },
    {
        id: 'meetings-booked',
        label: 'Meetings Booked',
        description: 'meeting-form',
        components: ['meeting-form'],
    },
    {
        id: 'issues-today',
        label: 'Issues Today',
        description: 'submit-issue',
        components: ['submit-issue'],
    },
];

const getDefaultDates = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);

    const toISODate = (date) => date.toISOString().split('T')[0];

    return {
        startDate: toISODate(start),
        endDate: toISODate(end),
    };
};

const aggregateComponentClicks = (docs = []) => {
    const componentTotals = {};

    docs.forEach((doc) => {
        const clicks = doc?.clicks || {};

        Object.entries(clicks).forEach(([component, featureMap]) => {
            const componentValue = Object.values(featureMap || {}).reduce(
                (sum, count) => sum + Number(count || 0),
                0,
            );

            componentTotals[component] = (componentTotals[component] || 0) + componentValue;
        });
    });

    return Object.entries(componentTotals).map(([component, clicks]) => ({ component, clicks }));
};

export default function AdminInteractions({ companyCode: companyCodeProp }) {
    const user = useContext(AuthContext);
    const analyticsToken = user?.analyticsToken;
    const refreshAnalyticsToken = user?.refreshAnalyticsToken;
    const [filters, setFilters] = useState(getDefaultDates);
    const [rawDocs, setRawDocs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [activeTab, setActiveTab] = useState('interactions');
    const [aiReportLoading, setAiReportLoading] = useState(false);
    const [aiReportMessage, setAiReportMessage] = useState('');
    const [aiReportError, setAiReportError] = useState('');
    const [reports, setReports] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [reportsError, setReportsError] = useState('');
    const [selectedReportIndex, setSelectedReportIndex] = useState(0);
    const derivedCompanyCode = user?.companycode ?? user?.companyCode;
    const companyCode = companyCodeProp || derivedCompanyCode;

    const resolveAnalyticsToken = useCallback(async () => {
        if (analyticsToken) {
            return analyticsToken;
        }

        if (typeof refreshAnalyticsToken === 'function') {
            try {
                return await refreshAnalyticsToken('admin-interactions-fetch');
            } catch (error) {
                console.error('[AdminInteractions] analytics token refresh failed', error);
                return null;
            }
        }

        return null;
    }, [analyticsToken, refreshAnalyticsToken]);

    const fetchAnalytics = useCallback(async () => {
        if (!companyCode) {
            setError('Missing company code. Please verify your account.');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const requestUrl = new URL(ANALYTICS_ENDPOINT);
            requestUrl.searchParams.set('companycode', companyCode);
            requestUrl.searchParams.set('startDate', filters.startDate);
            requestUrl.searchParams.set('endDate', filters.endDate);

            const token = await resolveAnalyticsToken();
            if (!token) {
                throw new Error('Missing analytics token. Please try again.');
            }

            const response = await fetch(requestUrl.toString(), {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                const message = `Analytics fetch failed (${response.status})`;
                throw new Error(message);
            }

            const payload = await response.json();
            setRawDocs(payload?.results || []);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('[AdminInteractions] fetch error', err);
            setError(err.message || 'Unable to load analytics right now');
        } finally {
            setIsLoading(false);
        }
    }, [companyCode, filters.endDate, filters.startDate, resolveAnalyticsToken]);

    const fetchReports = useCallback(async () => {
        if (!companyCode) {
            setReportsError('Missing company code. Please verify your account.');
            return;
        }

        setReportsLoading(true);
        setReportsError('');

        try {

            const response = await fetch('https://api.insightos.co.uk/api/showinteractionreport', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ companyCode }),
            });

            if (!response.ok) {
                throw new Error(`Report fetch failed (${response.status})`);
            }

            const payload = await response.json();
            const rawReports = payload?.reports ?? payload?.data ?? [];
            const normalizedReports =
                Array.isArray(rawReports) && rawReports.length === 2 && Array.isArray(rawReports[0])
                    ? rawReports[0]
                    : rawReports;

            setReports(Array.isArray(normalizedReports) ? normalizedReports : []);
            setSelectedReportIndex(0);
        } catch (err) {
            console.error('[AdminInteractions] report fetch error', err);
            setReports([]);
            setReportsError(err.message || 'Unable to load AI reports right now');
        } finally {
            setReportsLoading(false);
        }
    }, [companyCode]);

    useEffect(() => {
        if (companyCode) {
            fetchAnalytics();
        }
    }, [companyCode, fetchAnalytics]);

    useEffect(() => {
        if (activeTab === 'reports' && companyCode) {
            fetchReports();
        }
    }, [activeTab, companyCode, fetchReports]);

    useEffect(() => {
        if (reports.length === 0 && selectedReportIndex !== 0) {
            setSelectedReportIndex(0);
        } else if (selectedReportIndex >= reports.length && reports.length > 0) {
            setSelectedReportIndex(0);
        }
    }, [reports, selectedReportIndex]);

    const parseDateValue = (value) => {
        if (!value) return null;
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return value;
        }

        if (typeof value === 'string') {
            try {
                const isoCandidate = parseISO(value);
                if (!Number.isNaN(isoCandidate.getTime())) {
                    return isoCandidate;
                }
            } catch (err) {
                // ignore parse error, fall through to new Date
            }
        }

        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    };

    const formatDateValue = (value, pattern = 'MMM d, yyyy') => {
        const date = parseDateValue(value);
        return date ? format(date, pattern) : null;
    };

    const formatDateTimeValue = (value) => {
        const date = parseDateValue(value);
        return date ? format(date, 'PPpp') : null;
    };

    const getReportRangeLabel = (report = {}, index = 0) => {
        const startRaw =
            report.FromDate ??
            report.fromDate ??
            report.StartDate ??
            report.startDate ??
            report.From_Date ??
            report.Start_Date;
        const endRaw =
            report.ToDate ??
            report.toDate ??
            report.EndDate ??
            report.endDate ??
            report.To_Date ??
            report.End_Date;

        const startLabel = formatDateValue(startRaw);
        const endLabel = formatDateValue(endRaw);

        if (startLabel && endLabel) {
            return `${startLabel} – ${endLabel}`;
        }
        if (endLabel) return endLabel;
        if (startLabel) return startLabel;
        return `Report ${index + 1}`;
    };

    const getReportTitle = (report = {}, index = 0) =>
        report.Title || report.title || report.ReportName || `Interaction Report ${index + 1}`;

    const getReportContent = (report = {}) =>
        report.Content || report.content || report.Summary || report.summary || 'No content provided.';

    const componentData = useMemo(() => {
        const aggregated = aggregateComponentClicks(rawDocs);
        return aggregated.sort((a, b) => b.clicks - a.clicks);
    }, [rawDocs]);

    const leaderboardData = useMemo(() => componentData.slice(0, 5), [componentData]);
    const totalClicks = useMemo(
        () => componentData.reduce((sum, datum) => sum + datum.clicks, 0),
        [componentData],
    );

    const highlightComponentData = useMemo(() => {
        const clickMap = componentData.reduce((acc, datum) => {
            acc[datum.component] = datum.clicks;
            return acc;
        }, {});
        return HIGHLIGHT_COMPONENT_GROUPS.map((group) => {
            const clicks = group.components.reduce((sum, key) => sum + (clickMap[key] || 0), 0);
            return { ...group, clicks };
        });
    }, [componentData]);

    const highlightMaxClicks = useMemo(
        () => highlightComponentData.reduce((max, group) => Math.max(max, group.clicks), 0),
        [highlightComponentData],
    );

    const bucketData = useMemo(() => {
        const totals = rawDocs.reduce((acc, doc) => {
            Object.entries(doc?.bucketTotals || {}).forEach(([bucketKey, count]) => {
                const clicks = Number(count || 0);
                acc[bucketKey] = (acc[bucketKey] || 0) + clicks;
            });
            return acc;
        }, {});

        return Object.entries(totals)
            .map(([bucketKey, clicks]) => ({
                bucketKey,
                label: BUCKET_LABELS[bucketKey] || bucketKey,
                clicks,
            }))
            .sort((a, b) => b.clicks - a.clicks);
    }, [rawDocs]);

    const bucketTotalClicks = useMemo(
        () => bucketData.reduce((sum, bucket) => sum + bucket.clicks, 0),
        [bucketData],
    );

    const bucketHeatmapData = useMemo(() => {
        if (bucketData.length === 0) return [];
        const maxClicks = Math.max(...bucketData.map((bucket) => bucket.clicks), 1);
        return bucketData.map((bucket) => {
            const ratio = bucket.clicks / (maxClicks || 1);
            const baseColor = BUCKET_COLOR_MAP[bucket.bucketKey] || DEFAULT_BUCKET_COLOR;
            const alpha = 0.25 + ratio * 0.65;
            return {
                ...bucket,
                ratio,
                color: `rgba(${baseColor}, ${alpha.toFixed(2)})`,
            };
        });
    }, [bucketData]);

    const bucketHeatmapMap = useMemo(() => (
        bucketHeatmapData.reduce((acc, bucket) => {
            acc[bucket.bucketKey] = bucket;
            return acc;
        }, {})
    ), [bucketHeatmapData]);

    const bucketRadarPoints = useMemo(() => (
        BUCKET_CIRCLE_POSITIONS.map((config) => {
            const bucket = bucketHeatmapMap[config.bucketKey] || {
                bucketKey: config.bucketKey,
                label: BUCKET_LABELS[config.bucketKey] || config.bucketKey,
                clicks: 0,
            };
            const clicks = Number(bucket.clicks || 0);
            const percentage = bucketTotalClicks ? (clicks / bucketTotalClicks) * 100 : 0;
            return {
                bucketKey: config.bucketKey,
                label: bucket.label,
                value: Number(percentage.toFixed(2)),
                clicks,
                baseColor: BUCKET_COLOR_MAP[config.bucketKey] || DEFAULT_BUCKET_COLOR,
            };
        })
    ), [bucketHeatmapMap, bucketTotalClicks]);

    const bucketRadarPolygon = useMemo(() => (
        bucketRadarPoints.length > 0 ? [...bucketRadarPoints, bucketRadarPoints[0]] : []
    ), [bucketRadarPoints]);

    const radarCategories = useMemo(
        () => bucketRadarPoints.map((point) => point.label || point.bucketKey),
        [bucketRadarPoints],
    );

    const handleDateChange = (event) => {
        const { name, value } = event.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
        // Removed stray token line
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        fetchAnalytics();
    };

    const handleGenerateAiReport = async () => {
        if (!companyCode) {
            setAiReportError('Missing company code. Please verify your account.');
            return;
        }

        setAiReportLoading(true);
        setAiReportError('');
        setAiReportMessage('');

        try {
            const response = await fetch('https://api.insightos.co.uk/api/interactionsai', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    companyCode,
                    filters,
                    componentData,
                    bucketData,
                    highlightComponentData,
                    totalClicks,
                    startDate: filters.startDate,
                    endDate: filters.endDate
                }),
            });

            if (!response.ok) {
                throw new Error(`AI report creation failed (${response.status})`);
            }

            const payload = await response.json();
            setAiReportMessage(payload?.message || 'AI report created successfully.');
            await fetchReports();
        } catch (err) {
            console.error('[AdminInteractions] AI report error', err);
            setAiReportError(err.message || 'Unable to create AI report.');
        } finally {
            setAiReportLoading(false);
        }
    };

    const renderEmptyState = () => (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center text-gray-500">
            <p className="text-lg font-medium">No analytics data for the selected range yet.</p>
            <p className="text-sm mt-2">Interact with tracked components to populate this dashboard.</p>
        </div>
    );

    const tabs = [
        { id: 'interactions', label: 'Interactions' },
        { id: 'reports', label: 'Reports' },
    ];

    const renderReportsSection = () => {
        const activeReport = reports[selectedReportIndex] || null;
        const createdAtLabel = activeReport
            ? formatDateTimeValue(activeReport.CreatedAt || activeReport.createdAt || activeReport.created_at)
            : null;
        const activeRangeLabel = activeReport ? getReportRangeLabel(activeReport, selectedReportIndex) : null;
        const activeContent = activeReport ? getReportContent(activeReport) : null;

        return (
            <div className="rounded-2xl bg-white p-6 shadow-sm space-y-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900">AI Interaction Reports</h2>
                        <p className="text-sm text-slate-500">
                            Latest InteractionAnalysis reports for {companyCode || 'your company'}.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={fetchReports}
                        className="w-full rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 sm:w-auto"
                        disabled={reportsLoading || !companyCode}
                    >
                        {reportsLoading ? 'Refreshing...' : 'Refresh Reports'}
                    </button>
                </div>

                {reportsError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {reportsError}
                    </div>
                )}

                {reportsLoading ? (
                    <div className="text-sm text-slate-500">Loading reports...</div>
                ) : reports.length === 0 ? (
                    <div className="text-sm text-slate-500">No reports available yet. Generate one from the Interactions tab.</div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex flex-wrap gap-2">
                            {reports.map((report, idx) => {
                                const isActive = idx === selectedReportIndex;
                                return (
                                    <button
                                        key={report.id ?? report.ReportID ?? report._id ?? idx}
                                        type="button"
                                        onClick={() => setSelectedReportIndex(idx)}
                                        className={`rounded-xl border px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
                                            isActive
                                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                                : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                                        }`}
                                    >
                                        {getReportRangeLabel(report, idx)}
                                    </button>
                                );
                            })}
                        </div>

                        {activeReport && (
                            <div className="rounded-2xl border border-slate-100 bg-slate-50 p-5 shadow-sm space-y-3">
                                <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div>
                                        <h3 className="text-lg font-semibold text-slate-900">
                                            {getReportTitle(activeReport, selectedReportIndex)}
                                        </h3>
                                        {activeRangeLabel && (
                                            <p className="text-sm text-slate-500">{activeRangeLabel}</p>
                                        )}
                                    </div>
                                    {createdAtLabel && (
                                        <span className="text-xs font-medium text-slate-500">{createdAtLabel}</span>
                                    )}
                                </div>
                                <p className="whitespace-pre-wrap text-sm text-slate-700">{activeContent}</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
        <div className="min-h-screen bg-slate-100 px-4 py-8 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-6xl space-y-8">
                <div className="rounded-2xl bg-white p-2 shadow-sm">
                    <div className="flex gap-2">
                        {tabs.map((tab) => {
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex-1 rounded-xl px-4 py-2 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
                                        isActive
                                            ? 'bg-indigo-600 text-white shadow'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {activeTab === 'interactions' ? (
                    <>
                <div className="rounded-2xl bg-white p-6 shadow-sm">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h1 className="text-2xl font-semibold text-slate-900">Component Interaction Analytics</h1>
                            <p className="text-sm text-slate-500">
                                Authenticated insights for {companyCode || 'your company'} between {filters.startDate} and {filters.endDate}.
                            </p>
                        </div>
                        {lastUpdated && (
                            <p className="text-sm text-slate-400">Last refreshed {lastUpdated.toLocaleString()}</p>
                        )}
                    </div>

                    <form
                        onSubmit={handleSubmit}
                        className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-4"
                    >
                        <label className="flex flex-col text-sm font-medium text-slate-600">
                            Start Date
                            <input
                                type="date"
                                name="startDate"
                                value={filters.startDate}
                                onChange={handleDateChange}
                                className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                max={filters.endDate}
                                required
                            />
                        </label>

                        <label className="flex flex-col text-sm font-medium text-slate-600">
                            End Date
                            <input
                                type="date"
                                name="endDate"
                                value={filters.endDate}
                                onChange={handleDateChange}
                                className="mt-1 rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                                min={filters.startDate}
                                required
                            />
                        </label>

                        <div className="flex flex-col text-sm font-medium text-slate-600">
                            Company Code
                            <input
                                type="text"
                                value={companyCode || ''}
                                readOnly
                                className="mt-1 cursor-not-allowed rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-500"
                            />
                        </div>

                        <div className="flex items-end">
                            <button
                                type="submit"
                                className="w-full rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white shadow-sm transition hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
                                disabled={isLoading || !companyCode}
                            >
                                {isLoading ? 'Loading…' : 'Refresh'}
                            </button>
                        </div>
                    </form>

                    {error && (
                        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <button
                            type="button"
                            onClick={handleGenerateAiReport}
                            className="w-full rounded-lg bg-purple-600 px-4 py-2 font-semibold text-white shadow-sm transition hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 sm:w-auto"
                            disabled={aiReportLoading || !companyCode}
                        >
                            {aiReportLoading ? 'Creating AI Report...' : 'Create AI Report'}
                        </button>
                        {aiReportMessage && (
                            <span className="text-sm font-medium text-emerald-600">{aiReportMessage}</span>
                        )}
                        {aiReportError && (
                            <span className="text-sm font-medium text-red-600">{aiReportError}</span>
                        )}
                    </div>
                </div>

                <div className="grid gap-6 lg:grid-cols-3">
                    <div className="rounded-2xl bg-white p-6 shadow-sm lg:col-span-2">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Component Click Volume</h2>
                                <p className="text-sm text-slate-500">Recharts horizontal bar graph aggregated across all features.</p>
                            </div>
                            <span className="text-sm font-medium text-indigo-600">{totalClicks.toLocaleString()} clicks</span>
                        </div>
                        <div className="mt-4 h-96">
                            {componentData.length === 0 ? (
                                renderEmptyState()
                            ) : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={componentData}
                                        layout="vertical"
                                        margin={{ top: 10, right: 20, left: 50, bottom: 10 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                        <XAxis type="number" stroke="#475569" />
                                        <YAxis dataKey="component" type="category" width={120} stroke="#475569" />
                                        <Tooltip cursor={{ fill: '#f1f5f9' }} formatter={(value) => [value, 'Clicks']} />
                                        <Bar dataKey="clicks" fill="#6366f1" radius={[0, 6, 6, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>

                    <div className="rounded-2xl bg-white p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-semibold text-slate-900">Component Leaderboard</h2>
                                <p className="text-sm text-slate-500">Top performers visualized with Victory charts.</p>
                            </div>
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                                Top {leaderboardData.length || 0}
                            </span>
                        </div>

                        <div className="mt-4 h-64">
                            {leaderboardData.length === 0 ? (
                                renderEmptyState()
                            ) : (
                                <VictoryChart
                                    theme={VictoryTheme.material}
                                    domainPadding={20}
                                    padding={{ top: 20, bottom: 60, left: 60, right: 20 }}
                                >
                                    <VictoryAxis tickFormat={(tick) => tick}
                                        style={{ tickLabels: { angle: -30, fontSize: 10, fill: '#475569' } }}
                                    />
                                    <VictoryAxis dependentAxis tickFormat={(tick) => `${tick}`} style={{ tickLabels: { fontSize: 10 } }} />
                                    <VictoryBar
                                        data={leaderboardData.map((item) => ({ x: item.component, y: item.clicks }))}
                                        cornerRadius={{ top: 4, bottom: 4 }}
                                        style={{ data: { fill: '#22d3ee', width: 20 } }}
                                        labels={({ datum }) => `${datum.x}: ${datum.y}`}
                                        labelComponent={<VictoryTooltip constrainToVisibleArea />}
                                    />
                                </VictoryChart>
                            )}
                        </div>

                        <ul className="mt-4 space-y-3">
                            {leaderboardData.map((item, index) => (
                                <li
                                    key={item.component}
                                    className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3"
                                >
                                    <div>
                                        <p className="text-sm font-semibold text-slate-900">{index + 1}. {item.component}</p>
                                        <p className="text-xs text-slate-500">{((item.clicks / totalClicks) * 100 || 0).toFixed(1)}% of clicks</p>
                                    </div>
                                    <span className="text-base font-bold text-slate-800">{item.clicks.toLocaleString()}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="rounded-2xl bg-white p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-slate-900">Bucket Distribution</h2>
                            <p className="text-sm text-slate-500">HR, Communication, and Task Management engagement.</p>
                        </div>
                        <span className="text-sm font-medium text-indigo-600">{bucketTotalClicks.toLocaleString()} clicks</span>
                    </div>

                    {bucketData.length === 0 ? (
                        <div className="mt-6 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                            No bucket-level activity for this range yet.
                        </div>
                    ) : (
                        <>
                            <div className="mt-6">
                                <p className="text-sm font-semibold text-slate-600">Heatmap</p>
                                <div className="mt-4 flex flex-col gap-6 lg:flex-row lg:items-center">
                                    <div className="mx-auto w-full max-w-md flex-1">
                                        <div className="rounded-3xl border border-slate-100 bg-gradient-to-b from-white via-slate-50 to-white p-4 shadow-inner">
                                            <VictoryChart
                                                polar
                                                theme={VictoryTheme.material}
                                                domain={{ y: [0, 100] }}
                                                padding={{ top: 50, bottom: 50, left: 70, right: 70 }}
                                                categories={{ x: radarCategories }}
                                            >
                                                <VictoryPolarAxis
                                                    style={{ tickLabels: { fill: '#0e64ddff', fontSize: 9, fontWeight: 500 } }}
                                                    tickValues={radarCategories}
                                                />
                                                
                                                <VictoryArea
                                                    data={bucketRadarPolygon.map((point) => ({ x: point.label, y: point.value }))}
                                                    style={{
                                                        data: {
                                                            fill: 'rgba(79, 70, 229, 0.25)',
                                                            stroke: '#4338ca',
                                                            strokeWidth: 2,
                                                        },
                                                    }}
                                                />
                                                <VictoryScatter
                                                    data={bucketRadarPoints.map((point) => ({ x: point.label, y: point.value, clicks: point.clicks ?? 0 }))}
                                                    size={6}
                                                    style={{ data: { fill: '#1d4ed8' }, labels: { fill: '#0f172a', fontSize: 10, fontWeight: 500 } }}
                                                    labels={({ datum }) => `${Math.round(datum.y)}% · ${datum.clicks ?? 0} clicks`}
                                                    labelComponent={<VictoryTooltip constrainToVisibleArea cornerRadius={8} flyoutStyle={{ fill: 'white', stroke: '#e2e8f0' }} />}
                                            />
                                            </VictoryChart>

                                            <div className="text-center text-sm text-slate-500">
                                                Total bucket clicks: <span className="font-semibold text-slate-900">{bucketTotalClicks.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex-1 space-y-3">
                                        {bucketRadarPoints.map((point) => (
                                            <div key={`${point.bucketKey}-legend`} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                                                <div className="flex items-center justify-between text-sm font-semibold text-slate-700">
                                                    <span className="flex items-center gap-2">
                                                        <span
                                                            className="inline-flex h-2.5 w-2.5 rounded-full"
                                                            style={{ backgroundColor: `rgb(${point.baseColor})` }}
                                                        />
                                                        {point.label}
                                                    </span>
                                                    <span className="text-slate-500">{Math.round(point.value)}%</span>
                                                </div>
                                                <div className="mt-2 h-2 w-full rounded-full bg-white">
                                                    <div
                                                        className="h-full rounded-full"
                                                        style={{ width: `${point.value}%`, backgroundColor: `rgba(${point.baseColor}, 0.8)` }}
                                                    />
                                                </div>
                                                <p className="mt-1 text-xs text-slate-500">
                                                    {(point.clicks ?? 0).toLocaleString()} clicks
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Detailed list removed to avoid duplicate percentage display */}
                        </>
                    )}
                </div>

            <div className="rounded-2xl bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-900">Key Activity Table</h2>
                        <p className="text-sm text-slate-500">Tracks specific data-components with visual click totals.</p>
                    </div>
                    <span className="text-sm font-medium text-slate-500">{highlightComponentData.reduce((sum, group) => sum + group.clicks, 0).toLocaleString()} tracked clicks</span>
                </div>

                <div className="mt-6 overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-100 text-sm">
                        <thead>
                            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                                <th className="py-2">Activity</th>
                                <th className="py-2">Data Components</th>
                                <th className="py-2 text-right">Clicks</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {highlightComponentData.map((group) => {
                                const widthPercent = highlightMaxClicks ? (group.clicks / highlightMaxClicks) * 100 : 0;
                                return (
                                    <tr key={group.id}>
                                        <td className="py-3 pr-4">
                                            <p className="text-sm font-semibold text-slate-900">{group.label}</p>
                                            <p className="text-xs text-slate-500">{group.description}</p>
                                        </td>
                                        <td className="py-3 pr-4 text-xs text-slate-500">{group.components.join(', ')}</td>
                                        <td className="py-3">
                                            <div className="text-right text-sm font-semibold text-slate-900">{group.clicks.toLocaleString()}</div>
                                            <div className="mt-2 h-2 rounded-full bg-slate-100">
                                                <div
                                                    className="h-full rounded-full bg-indigo-500"
                                                    style={{ width: `${widthPercent}%` }}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
                </>
                ) : (
                    renderReportsSection()
                )}
            </div>
        </div>
        </>
        );
}
